import { html, css, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSAutoPaymentsCard from './card-base.js';
import JobSchema from '../../../../../models/job-schema.js';
import definition from '@job-schemas/public-ledger/auto-payments/resign-allowance.json';
import { jobSchema } from '../../../job-schema.js';
import { AUTOPAY_CARD_OUTCOMES } from './presentations.js';
import {
  approveSummaryRows,
  type AutoPaymentRow,
  type ProposedSettings,
} from '../../../../../helpers/auto-payments.js';
import { resignAllowance } from '../../../../../helpers/auto-payments-api.js';
import { resignStage } from '../../../../../helpers/auto-payment-cards.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';

// The live settings as a proposal, so the summary shows them as the signing card will
function liveSettings(row: AutoPaymentRow | null): ProposedSettings | null {
  if (!row) return null;
  return {
    companyThresholdCredits: row.companyThresholdCredits,
    companyTargetPence: row.companyTargetPence,
    operatingThresholdCredits: row.operatingThresholdCredits,
    operatingTargetCredits: row.operatingTargetCredits,
    monthlyCardLimitPence: row.monthlyCardLimitPence,
    monthlyTopUpLimitPence: row.monthlyTopUpLimitPence,
    targetAlertPercent: row.targetAlertPercent ?? null,
    card: {
      cardOwnerId: row.cardOwnerId,
      cardBrand: row.cardBrand,
      cardLast4: row.cardLast4,
      cardExpMonth: row.cardExpMonth,
      cardExpYear: row.cardExpYear,
    },
  };
}

/**
 * Re-sign the automatic payments allowance (#25 phase 4): the company's allowance to its operating account is missing on
 * the public ledger, so automatic top-ups have paused. One open card per company, claimed by a signatory, who proposes the
 * same maximum allowance again with the company's current settings, unchanged (public-ledger/autopay/resign, which
 * completes the card `proposed`). The workflow then schedules it, paid by nodeStream's platform account like set-up
 * (DECISION 4.1, settled 2026-10-27), and every
 * signatory signs it on their collect-signature card, through the same signature-only path as the set-up; each browser
 * first checks on the public ledger that the allowance is to the company's own operating account.
 * A signed stop (#40) closes it `stopped` (public-ledger/autopay/stop-finish).
 */
@customElement(NSPublicLedgerAutoPaymentsResignAllowance.is)
export default class NSPublicLedgerAutoPaymentsResignAllowance extends NSAutoPaymentsCard {
  static get is() {
    return 'ns-public-ledger-auto-payments-resign-allowance';
  }

  static get schema(): JobSchema {
    // Every outcome is hidden: presentations.ts says whose each one is
    return jobSchema(definition, {
      outcomes: AUTOPAY_CARD_OUTCOMES['@ns/public-ledger-auto-payments-resign-allowance'],
      icon: 'bank-card',
    });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  protected async _loadRow() {
    await super._loadRow();
    await this._loadPerson(this._row?.cardOwnerId);
  }

  private async __propose() {
    const job = this._job;
    if (!job) return;
    await this._serverAction(
      async () => {
        await resignAllowance(this._auth?.token, job.id);
        return 'Proposed. Every signatory is asked to sign the allowance again, and this job closes in a moment.';
      },
      'Proposing the allowance',
      'The allowance could not be proposed. Please try again.',
    );
  }

  private __renderStage(): TemplateResult {
    switch (resignStage(this._row)) {
      case 'NOT_ACTIVE':
        return html`
          <p>Automatic payments aren’t on for your company, so there is no allowance to sign again.</p>
        `;
      case 'IN_PLACE':
        return html`
          <p>The allowance is in place on the public ledger again, so there is nothing to sign.</p>
        `;
      case 'CHANGE_OPEN':
        return html`
          <p>
            Settings are already waiting for the signatories to sign. Once that has finished, you can propose the
            allowance again if it’s still missing.
          </p>
        `;
      default:
        return html`
          <p>
            Propose it again and every signatory is asked to sign it. The settings stay as they are. Top-ups start again
            once it has run on the public ledger.
          </p>
        `;
    }
  }

  render() {
    const rows = approveSummaryRows(liveSettings(this._row));
    return html`
      <ns-job-layout>
        <p slot="intro">
          The allowance automatic top-ups draw on is missing on the public ledger, so your company’s operating account
          isn’t being topped up.
        </p>
        ${this._renderWaiting() ??
        (this._claimed || this._completed
          ? html`
              <div class="job-panel">
                <h3>Sign the allowance again</h3>
                ${this.__renderStage()}
              </div>
            `
          : this._renderClaimNote('propose the allowance again'))}
        ${rows
          ? html`
              <div class="job-panel">
                <h3>Your company’s settings</h3>
                <ns-detail-list .rows=${rows}></ns-detail-list>
              </div>
            `
          : nothing}
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>About the allowance</h3>
          <p>
            Signed by the signatories, the allowance lets your company’s operating account draw credits from its company
            account on the public ledger, within these settings. Without it the operating account can’t be topped up, so
            public ledger actions stop once it runs low.
          </p>
          <p>nodeStream pays the public ledger fees for this signing.</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this._completed || this._waiting !== null) return nothing;
    if (!this._claimed) return this._renderClaimButton();
    if (resignStage(this._row) !== 'READY') return nothing;
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this.__propose()}>
        Propose the allowance
      </wa-button>
    `;
  }
}
